/**
 * TASK-068 — voler.emovia.com murmuration simulation.
 *
 * Pure boids simulation (separation / alignment / cohesion + wander), plus an
 * external wind force and a target-arrival mode used to compose words. No DOM,
 * no React: input is state + forces, output is mutated state. This keeps the
 * physics unit-testable and the React component a thin canvas orchestrator.
 *
 * Performance: neighbour lookups use a uniform spatial hash grid (O(n) average)
 * instead of the naive O(n²) all-pairs scan, so the flock scales to a few
 * thousand boids at 60fps.
 */

export type Vec = { x: number; y: number };

export type Boid = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  // Per-boid jitter so the flock never looks like a uniform swarm.
  maxSpeed: number;
  // Depth fudge in [0,1]: drives size + alpha for a parallax-ish feel.
  depth: number;
  // Stable wander phase so each boid drifts on its own little arc.
  wanderAngle: number;
  // Index of this boid's assigned target point during a formation (-1 = free).
  target: number;
  // Brand-tint amount in [0,1] applied while EMOVIA letters settle.
  tint: number;
};

export type FlockConfig = {
  perceptionRadius: number;
  separationRadius: number;
  separationWeight: number;
  alignmentWeight: number;
  cohesionWeight: number;
  wanderWeight: number;
  maxForce: number;
  // Soft boundary: how far from the edge the steer-back force kicks in.
  margin: number;
  edgeWeight: number;
};

export type WindConfig = {
  radius: number;
  strength: number;
};

export type Bounds = { width: number; height: number };

// Tuned by hand against the murmuration brief: wide perception so density waves
// propagate, separation kept tight so the flock can pack without exploding,
// cohesion gentle so it never collapses into a blob, wander low so motion reads
// organic rather than noisy. This is where the "alive" quality lives.
export const DEFAULT_FLOCK: FlockConfig = {
  perceptionRadius: 64,
  separationRadius: 11,
  separationWeight: 1.6,
  alignmentWeight: 1.5,
  cohesionWeight: 1.35,
  wanderWeight: 0.35,
  maxForce: 0.09,
  margin: 110,
  edgeWeight: 0.5,
};

export const DEFAULT_WIND: WindConfig = {
  radius: 150,
  strength: 1.4,
};

// --- small vector helpers (kept inline-cheap, no allocations in hot paths) ---

function limit(x: number, y: number, max: number): Vec {
  const m2 = x * x + y * y;
  if (m2 > max * max && m2 > 0) {
    const inv = max / Math.sqrt(m2);
    return { x: x * inv, y: y * inv };
  }
  return { x, y };
}

/** Deterministic-ish RNG so tests can seed a reproducible flock. */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    // xorshift32
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
}

export function createFlock(count: number, bounds: Bounds, rng: () => number): Boid[] {
  const boids: Boid[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const angle = rng() * Math.PI * 2;
    const speed = 1.2 + rng() * 1.4;
    boids[i] = {
      x: rng() * bounds.width,
      y: rng() * bounds.height,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      maxSpeed: 2.4 + rng() * 1.6,
      depth: rng(),
      wanderAngle: rng() * Math.PI * 2,
      target: -1,
      tint: 0,
    };
  }
  return boids;
}

// --- spatial hash grid: rebuilt each frame, O(n) ---

type Grid = {
  cell: number;
  cols: number;
  rows: number;
  buckets: number[][];
};

function buildGrid(boids: Boid[], bounds: Bounds, cell: number): Grid {
  const cols = Math.max(1, Math.ceil(bounds.width / cell));
  const rows = Math.max(1, Math.ceil(bounds.height / cell));
  const buckets: number[][] = new Array(cols * rows);
  for (let i = 0; i < buckets.length; i++) buckets[i] = [];
  for (let i = 0; i < boids.length; i++) {
    const cx = Math.min(cols - 1, Math.max(0, Math.floor(boids[i].x / cell)));
    const cy = Math.min(rows - 1, Math.max(0, Math.floor(boids[i].y / cell)));
    buckets[cy * cols + cx].push(i);
  }
  return { cell, cols, rows, buckets };
}

/**
 * Advance the flock one step. Mutates `boids` in place. `windActive` lets the
 * caller damp the wind during a formation so the word doesn't shred on hover.
 */
export function step(
  boids: Boid[],
  bounds: Bounds,
  cfg: FlockConfig,
  wind: WindConfig,
  windPoint: Vec | null,
  windScale: number,
  targets: Float32Array | null,
  tailMask: Uint8Array | null,
): void {
  const grid = buildGrid(boids, bounds, cfg.perceptionRadius);
  const pr2 = cfg.perceptionRadius * cfg.perceptionRadius;
  const sr2 = cfg.separationRadius * cfg.separationRadius;

  for (let i = 0; i < boids.length; i++) {
    const b = boids[i];

    let sepX = 0;
    let sepY = 0;
    let aliX = 0;
    let aliY = 0;
    let cohX = 0;
    let cohY = 0;
    let count = 0;

    const cx = Math.min(grid.cols - 1, Math.max(0, Math.floor(b.x / grid.cell)));
    const cy = Math.min(grid.rows - 1, Math.max(0, Math.floor(b.y / grid.cell)));

    // Scan the 3×3 neighbourhood of cells around the boid.
    for (let gy = cy - 1; gy <= cy + 1; gy++) {
      if (gy < 0 || gy >= grid.rows) continue;
      for (let gx = cx - 1; gx <= cx + 1; gx++) {
        if (gx < 0 || gx >= grid.cols) continue;
        const bucket = grid.buckets[gy * grid.cols + gx];
        for (let k = 0; k < bucket.length; k++) {
          const j = bucket[k];
          if (j === i) continue;
          const o = boids[j];
          const dx = b.x - o.x;
          const dy = b.y - o.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > pr2 || d2 === 0) continue;

          // Separation: stronger the closer the neighbour (inverse weighting).
          if (d2 < sr2) {
            const inv = 1 / Math.sqrt(d2);
            sepX += (dx * inv) / Math.sqrt(d2);
            sepY += (dy * inv) / Math.sqrt(d2);
          }
          aliX += o.vx;
          aliY += o.vy;
          cohX += o.x;
          cohY += o.y;
          count++;
        }
      }
    }

    let accX = 0;
    let accY = 0;

    // When a boid is locked onto a word target, fade the flocking forces right
    // down so arrival dominates and the letters read crisp instead of fuzzy.
    const flockScale = targets && b.target >= 0 ? 0.12 : 1;

    if (count > 0) {
      // Alignment: steer toward neighbours' average heading.
      const ali = limit(aliX / count, aliY / count, cfg.maxForce * 8);
      accX += ali.x * cfg.alignmentWeight * 0.02 * flockScale;
      accY += ali.y * cfg.alignmentWeight * 0.02 * flockScale;

      // Cohesion: steer toward neighbours' centre of mass.
      const cohDX = cohX / count - b.x;
      const cohDY = cohY / count - b.y;
      const coh = limit(cohDX, cohDY, cfg.maxForce * 8);
      accX += coh.x * cfg.cohesionWeight * 0.012 * flockScale;
      accY += coh.y * cfg.cohesionWeight * 0.012 * flockScale;
    }

    // Separation: kept (lightly) even in formation so boids don't fully stack.
    const sep = limit(sepX, sepY, cfg.maxForce * 12);
    const sepScale = targets && b.target >= 0 ? 0.4 : 1;
    accX += sep.x * cfg.separationWeight * 0.6 * sepScale;
    accY += sep.y * cfg.separationWeight * 0.6 * sepScale;

    // Wander: each boid eases its heading along a slowly rotating offset.
    b.wanderAngle += (Math.random() - 0.5) * 0.3;
    accX += Math.cos(b.wanderAngle) * cfg.wanderWeight * 0.03 * flockScale;
    accY += Math.sin(b.wanderAngle) * cfg.wanderWeight * 0.03 * flockScale;

    // Soft edges: steer back in instead of wrapping, which keeps the silhouette.
    // Skipped in formation so boids can settle on letters near the margins.
    if (!(targets && b.target >= 0)) {
      if (b.x < cfg.margin) accX += cfg.edgeWeight * (1 - b.x / cfg.margin);
      else if (b.x > bounds.width - cfg.margin)
        accX -= cfg.edgeWeight * (1 - (bounds.width - b.x) / cfg.margin);
      if (b.y < cfg.margin) accY += cfg.edgeWeight * (1 - b.y / cfg.margin);
      else if (b.y > bounds.height - cfg.margin)
        accY -= cfg.edgeWeight * (1 - (bounds.height - b.y) / cfg.margin);
    }

    // Wind: radial repulsion with a smooth quadratic falloff.
    if (windPoint && windScale > 0) {
      const dx = b.x - windPoint.x;
      const dy = b.y - windPoint.y;
      const d2 = dx * dx + dy * dy;
      const r2 = wind.radius * wind.radius;
      if (d2 < r2 && d2 > 0) {
        const d = Math.sqrt(d2);
        const falloff = 1 - d / wind.radius;
        const f = (falloff * falloff * wind.strength * windScale) / d;
        accX += dx * f;
        accY += dy * f;
      }
    }

    // Formation: arrival steering toward this boid's assigned target point.
    const inFormation = !!(targets && b.target >= 0);
    if (inFormation && targets) {
      const tx = targets[b.target * 2];
      const ty = targets[b.target * 2 + 1];
      const dx = tx - b.x;
      const dy = ty - b.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      // Arrival: glide in fast, brake hard inside a tight slowing radius so the
      // letters land sharp. Boids may exceed cruise speed while converging.
      const formSpeed = b.maxSpeed * 2.4;
      const slowing = 26;
      const desired = d > 0 ? (d < slowing ? (d / slowing) * formSpeed : formSpeed) : 0;
      if (d > 0) {
        const ndx = (dx / d) * desired;
        const ndy = (dy / d) * desired;
        const steer = limit(ndx - b.vx, ndy - b.vy, cfg.maxForce * 60);
        accX += steer.x;
        accY += steer.y;
      }
      // Only EMOVIA-tail boids drift toward the brand colour.
      if (tailMask && tailMask[b.target]) b.tint += (1 - b.tint) * 0.05;
      else if (b.tint > 0) b.tint *= 0.92;
    } else if (b.tint > 0) {
      b.tint *= 0.92;
    }

    b.vx += accX;
    b.vy += accY;
    // Let formation boids move faster than cruise so the word snaps together.
    const cap = inFormation ? b.maxSpeed * 2.4 : b.maxSpeed;
    const v = limit(b.vx, b.vy, cap);
    b.vx = v.x;
    b.vy = v.y;
    b.x += b.vx;
    b.y += b.vy;
  }
}

/**
 * Sample lit pixels of a rendered string into a flat [x,y,x,y,...] array.
 * The caller provides an offscreen 2D context already sized to `bounds`; this
 * function only draws + reads, so it can be tested against a mock context.
 *
 * `step` controls sampling density (px). Lower = more points = denser word.
 */
export function sampleText(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  bounds: Bounds,
  fontPx: number,
  sampleStep: number,
): Float32Array {
  ctx.clearRect(0, 0, bounds.width, bounds.height);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;

  const lineHeight = fontPx * 1.04;
  const totalH = lineHeight * lines.length;
  const startY = bounds.height / 2 - totalH / 2 + lineHeight / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], bounds.width / 2, startY + i * lineHeight);
  }

  const img = ctx.getImageData(0, 0, bounds.width, bounds.height).data;
  const pts: number[] = [];
  for (let y = 0; y < bounds.height; y += sampleStep) {
    for (let x = 0; x < bounds.width; x += sampleStep) {
      const alpha = img[(y * bounds.width + x) * 4 + 3];
      if (alpha > 128) {
        pts.push(x, y);
      }
    }
  }
  return new Float32Array(pts);
}

/**
 * Sample a two-part word ("VOLER." + "EMOVIA") in one pass and return both the
 * point cloud and a tail mask (1 where the point belongs to the second part).
 * Drawing each part in a distinct channel avoids guessing a tail x-threshold,
 * so it works for single-line ("VOLER.EMOVIA") and stacked layouts alike.
 */
export function sampleTwoTone(
  ctx: CanvasRenderingContext2D,
  headLines: string[],
  tailLines: string[],
  bounds: Bounds,
  fontPx: number,
  sampleStep: number,
): { targets: Float32Array; tailMask: Uint8Array } {
  // Red channel = head (VOLER.), green channel = tail (EMOVIA). Both rendered
  // with identical font metrics & layout so they join seamlessly.
  ctx.clearRect(0, 0, bounds.width, bounds.height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;

  const stacked = headLines.length + tailLines.length > 1;
  const lineHeight = fontPx * 1.04;

  if (stacked) {
    // Two visual rows: head on top, tail below.
    const totalH = lineHeight * 2;
    const startY = bounds.height / 2 - totalH / 2 + lineHeight / 2;
    ctx.fillStyle = '#f00';
    ctx.fillText(headLines.join(''), bounds.width / 2, startY);
    ctx.fillStyle = '#0f0';
    ctx.fillText(tailLines.join(''), bounds.width / 2, startY + lineHeight);
  } else {
    // Single line: measure both parts so the whole word stays centred.
    const head = headLines.join('');
    const tail = tailLines.join('');
    const wHead = ctx.measureText(head).width;
    const wTail = ctx.measureText(tail).width;
    const total = wHead + wTail;
    const left = bounds.width / 2 - total / 2;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#f00';
    ctx.fillText(head, left, bounds.height / 2);
    ctx.fillStyle = '#0f0';
    ctx.fillText(tail, left + wHead, bounds.height / 2);
    ctx.textAlign = 'center';
  }

  const img = ctx.getImageData(0, 0, bounds.width, bounds.height).data;
  const pts: number[] = [];
  const tails: number[] = [];
  for (let y = 0; y < bounds.height; y += sampleStep) {
    for (let x = 0; x < bounds.width; x += sampleStep) {
      const o = (y * bounds.width + x) * 4;
      const r = img[o];
      const g = img[o + 1];
      if (r > 100 || g > 100) {
        pts.push(x, y);
        tails.push(g > r ? 1 : 0);
      }
    }
  }
  return { targets: new Float32Array(pts), tailMask: Uint8Array.from(tails) };
}

/**
 * Assign each boid the nearest-ish target index, cycling so every target point
 * is covered when there are fewer boids than points (and vice versa). Returns
 * the per-target marker (1 = part of the EMOVIA tail, 0 = rest) so the renderer
 * knows which boids to tint. `tailFromX` is the x beyond which a point belongs
 * to the brand-coloured tail.
 */
export function assignTargets(boids: Boid[], targets: Float32Array, rng: () => number): void {
  const pointCount = targets.length / 2;
  if (pointCount === 0) {
    for (let i = 0; i < boids.length; i++) boids[i].target = -1;
    return;
  }
  // Shuffle a point index pool for an even, organic fill.
  const order = new Array(pointCount);
  for (let i = 0; i < pointCount; i++) order[i] = i;
  for (let i = pointCount - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = order[i];
    order[i] = order[j];
    order[j] = t;
  }
  for (let i = 0; i < boids.length; i++) {
    boids[i].target = order[i % pointCount];
  }
}

export function releaseTargets(boids: Boid[]): void {
  for (let i = 0; i < boids.length; i++) boids[i].target = -1;
}
